import { Sentry } from '@opendoor/observability';
import { createApi, skipToken } from '@reduxjs/toolkit/query/react';
import type { DocumentNode, OperationDefinitionNode } from 'graphql';
import { ClientError, GraphQLClient } from 'graphql-request';

import {
  GoogleProtobuf_StructInput,
  OdProtosPartnershipData_PartnershipType,
  OdProtosReceptionServices_SellerAddressInput,
  OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product,
} from 'src/__generated__/globalTypes';
import {
  CreateSellerInputWithTrackingDataFromCookies,
  CreateSellerInputWithTrackingDataFromCookiesVariables,
} from 'src/api/mutations/__generated__/CreateSellerInputWithTrackingDataFromCookies';
import { ModifySellerAddress } from 'src/api/mutations/__generated__/ModifySellerAddress';
import { ResendListedOfferEmail } from 'src/api/mutations/__generated__/ResendListedOfferEmail';
import { SaveSellerInputProgress } from 'src/api/mutations/__generated__/SaveSellerInputProgress';
import {
  SetImpressionRequest,
  SetImpressionRequestVariables,
} from 'src/api/mutations/__generated__/SetImpressionRequest';
import { UpdateAnswers } from 'src/api/mutations/__generated__/UpdateAnswers';
import {
  VerifyAddress,
  VerifyAddressVariables,
} from 'src/api/mutations/__generated__/VerifyAddress';
import createSellerInputWithTrackingDataFromCookies from 'src/api/mutations/createSellerInputWithTrackingDataFromCookies';
import modifySellerAddress from 'src/api/mutations/modifySellerAddress';
import resendListedOfferEmail from 'src/api/mutations/resendListedOfferEmail';
import saveSellerInputProgress from 'src/api/mutations/saveSellerInputProgress';
import setImpression from 'src/api/mutations/setImpression';
import updateAnswersMutation from 'src/api/mutations/updateAnswers';
import verifyAddressMutation from 'src/api/mutations/verifyAddress';
import {
  GetAmplitudeFeatureFlagsRequest,
  GetAmplitudeFeatureFlagsRequestVariables,
} from 'src/api/queries/__generated__/GetAmplitudeFeatureFlagsRequest';
import { GetCustomerUuidForSellerInput } from 'src/api/queries/__generated__/GetCustomerUuidForSellerInput';
import { GetEmailValidationQuery } from 'src/api/queries/__generated__/GetEmailValidationQuery';
import { GetSellerLeadQuery } from 'src/api/queries/__generated__/GetSellerLeadQuery';
import {
  GetVariantRequest,
  GetVariantRequestVariables,
} from 'src/api/queries/__generated__/GetVariantRequest';
import {
  ListMlsesQuery,
  ListMlsesQuery_webMarket_listMlses_mlses_mlses,
} from 'src/api/queries/__generated__/ListMlsesQuery';
import {
  ListPartnersQuery,
  ListPartnersQuery_partnership_listPartners_partners,
} from 'src/api/queries/__generated__/ListPartnersQuery';
import {
  RequestListedOffer,
  RequestListedOffer_reception_requestListedOffer_sellerInput,
  RequestListedOfferVariables,
} from 'src/api/queries/__generated__/RequestListedOffer';
import { TriggerExperimentImpressionQuery } from 'src/api/queries/__generated__/TriggerExperimentImpressionQuery';
import { Zones, Zones_buyer_getZonesByTextQuery_zones } from 'src/api/queries/__generated__/Zones';
import getAmplitudeFeatureFlagsQuery from 'src/api/queries/getAmplitudeFeatureFlags';
import getCustomerUuidForSellerInput from 'src/api/queries/getCustomerUuid';
import getEmailValidationQuery from 'src/api/queries/getEmailValidation';
import getSellerInputUuidQuery from 'src/api/queries/getSellerInputUuid';
import getSellerLeadQuery from 'src/api/queries/getSellerLead';
import getValidataQuery from 'src/api/queries/getValidata';
import getVariantQuery from 'src/api/queries/getVariant';
import listMlsesQuery from 'src/api/queries/listMlses';
import listPartnersQuery from 'src/api/queries/listPartners';
import requestListedOfferQuery from 'src/api/queries/requestListedOffer';
import triggerExperimentImpressionQueryBody from 'src/api/queries/triggerExperimentImpression';
import { channelStringToEnum } from 'src/api/sellerInputToLead';

import {
  getHuman_web_userSession_human,
  getHuman as getHumanQueryData,
} from '../api/queries/__generated__/getHuman';
import {
  GetSellerInputQuery as GetSellerInputQueryData,
  GetSellerInputQueryVariables,
} from '../api/queries/__generated__/GetSellerInputQuery';
import { GetSellerInputUuidQuery as GetSellerInputUuidQueryData } from '../api/queries/__generated__/GetSellerInputUuidQuery';
import {
  GetValidataQuery,
  GetValidataQuery_validata_getConfidenceSignalsByFields_confidenceSignals,
} from '../api/queries/__generated__/GetValidataQuery';
import {
  VerifyActiveOffer,
  VerifyActiveOffer_sellDirect_verifyActiveOffer_offer,
} from '../api/queries/__generated__/VerifyActiveOffer';
import getHuman from '../api/queries/getHuman';
import getSellerInputQuery from '../api/queries/getSellerInput';
import getActiveOffer from '../api/queries/verifyActiveOffer';
import getZones from '../api/queries/zones';
import { ExperimentFlag, ExperimentVariant, getExperimentKey } from './experimentFlags';
import { getFeatureFlagNames } from './featureFlags';
import { UpdatedAddress } from './sellerFlow/actions';
import { SellerInputAddress } from './sellerInput';

type GqlQueryCall<T = Record<string, any>> = {
  body: DocumentNode;
  variables?: T;
};

const getQueryName = (node: DocumentNode): string => {
  const opDef = node.definitions.find(
    (def) => def.kind === 'OperationDefinition',
  ) as OperationDefinitionNode;
  if (opDef) {
    return opDef.name?.value ?? 'unnamed query';
  }
  return 'query without operation definition';
};

// Add GQL errors that we should not log in sentry here. For example
// GetSellerInputQuery frequently returns an error. This is an unimportant error that can be ignored.
// Errors can still be detected and handled when using hooks (ie `const { data,
// error} = GetSellerInputQuery(...)`). This only prevents automatic
// logging to Sentry
const ignoreGqlErrors = {
  GetSellerInputQuery: ['NOT_FOUND', 'malformed UUID'],
  GetSellerLeadQuery: ['NOT_FOUND'],

  // we see this frequently, we show the user that there's an offer, but when
  // they enter an email address it does not match, there's nothing for us to do
  // so we should skip sending to sentry
  VerifyActiveOffer: ['Email does not match customer'],

  // TODO: Ignore this error for now, but we should clean up our calls to only
  // happen when the identifier is set
  GetVariantRequest: ['AnonymousIdentifier cannot be empty'],

  // ignore when users try to save with an invalid email
  SaveSellerInputProgress: ['invalid email'],
};

export const graphqlBaseQuery =
  ({ baseUrl }: { baseUrl: string }) =>
  async ({ body, variables = {} }: GqlQueryCall) => {
    let retries = 4;
    while (retries) {
      try {
        // well if we have to say we're apollo then we will...
        const headers = {
          'apollographql-client-name': 'reception-fe-redux-tk',
          'apollographql-client-version': '1.0',
        };

        const client = new GraphQLClient(baseUrl, {
          credentials: 'include',
          mode: 'cors',
        });

        const result = await client.request(body, variables, headers);
        return { data: result };
      } catch (error) {
        if (error instanceof ClientError) {
          // The gql server returned an error to us. This is possibly a syntax
          // error, incorrect/missing variables, or maybe just that the query
          // return no result and decided to return an error about that
          //
          // No retries for this type of error (retries for this type of error
          // should be handled as a special case in the calling component)
          //
          const ignoreError = (ignoreGqlErrors[getQueryName(body)] ?? []).some(
            (errMessage: string) => error.message.indexOf(errMessage) !== -1,
          );

          if (!ignoreError) {
            Sentry.withScope((scope) => {
              const { message, path } = error;
              scope.setTags({ type: 'graphql-operation' });
              scope.setExtras({
                message,
                path,
                operationName: getQueryName(body),
                variables: variables,
              });
              Sentry.captureMessage(`GraphQL error ${getQueryName(body)}: ${message}`);
            });

            // eslint-disable-next-line no-console
            console.error(`Query "${getQueryName(body)}" returned client error with ${error}`, {
              variables,
            });
          } else {
            // eslint-disable-next-line no-console
            console.info(`GQL Error [not logged to sentry]: Query "${getQueryName(body)}"`, {
              error,
              variables,
            });
          }

          const queryError = {
            status: error.response.status,
            data: { message: `${error}` },
          };

          // RTK Query expects this function to return an object with either
          // {data} or {error} - this is separate from GQL's errors object. See:
          // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#basequery-function-return-value
          return { error: queryError };
        }
        if (retries == 1) {
          // We're experiencing some error that is not a gql syntax/service related issue,
          // most likely a network problem, so we automatically retry a few times before
          // giving up and logging an error

          Sentry.withScope((scope) => {
            const { message, name, stack } = error;
            scope.setTags({ type: 'graphql-operation' });
            scope.setExtras({ message, name, stack });
            Sentry.captureMessage(`Network error ${getQueryName(body)}: ${message}`);
          });

          // eslint-disable-next-line no-console
          console.log(`Query "${getQueryName(body)}" failed with ${error}`, { variables });
          // eslint-disable-next-line no-console
          console.error('Unable to compelete GQL query', error);
          const queryError = {
            status: 500,
            data: { message: `${error}` },
          };
          return { error: queryError };
        }
      }
      retries--;
    }

    // eslint-disable-next-line no-console
    console.error(`Query "${getQueryName(body)}" failed with no error?`, { variables });
    return { error: { status: 500, data: 'Request failed' } };
  };

export const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API_URL ?? '/api/graphql';

export const api = createApi({
  reducerPath: 'gql',
  baseQuery: graphqlBaseQuery({ baseUrl: GRAPHQL_API_URL }),
  endpoints: (builder) => ({
    getSellerLead: builder.query<GetSellerLeadQuery, string>({
      query: (uuid) => ({
        body: getSellerLeadQuery,
        variables: {
          sellerInputUUID: uuid,
        },
      }),
    }),
    getHuman: builder.query<getHuman_web_userSession_human | null, void>({
      query: () => ({
        body: getHuman,
      }),
      transformResponse: (response: getHumanQueryData) => {
        return response.web.userSession?.human ?? null;
      },
    }),
    getCustomerUuid: builder.query<string | null, string>({
      query: (id) => ({
        body: getCustomerUuidForSellerInput,
        variables: {
          sellerInputUUID: id,
        },
      }),
      transformResponse: (response: GetCustomerUuidForSellerInput) => {
        return response.reception.getSellerInput?.sellerInput?.customerUuid ?? null;
      },
    }),
    getSellerInputs: builder.query<GetSellerInputQueryData, string>({
      query: (id): GqlQueryCall<GetSellerInputQueryVariables> => ({
        body: getSellerInputQuery,
        variables: {
          sellerInputUUID: id,
          featureFlagNames: getFeatureFlagNames(),
        },
      }),
    }),
    getSellerInputUuid: builder.query<string, string>({
      query: (token) => ({
        body: getSellerInputUuidQuery,
        variables: {
          input: {
            lead: {
              token,
            },
          },
        },
      }),
      transformResponse: (response: GetSellerInputUuidQueryData) => {
        return response.sellDirect.getLead?.lead?.sellerInputUuid ?? '';
      },
    }),
    getBuilderPartners: builder.query<
      Array<ListPartnersQuery_partnership_listPartners_partners>,
      void
    >({
      query: () => ({
        body: listPartnersQuery,
        variables: {
          partnershipType: OdProtosPartnershipData_PartnershipType.HOME_BUILDER,
        },
      }),
      transformResponse: (response: ListPartnersQuery) => {
        return response.partnership?.listPartners?.partners ?? [];
      },
    }),
    getMlses: builder.query<Array<ListMlsesQuery_webMarket_listMlses_mlses_mlses>, string>({
      query: (zipcode) => ({
        body: listMlsesQuery,
        variables: {
          postalCode: zipcode,
        },
      }),
      transformResponse: (response: ListMlsesQuery) => {
        return response.webMarket?.listMlses?.mlses?.mlses ?? [];
      },
    }),
    getValidata: builder.query<
      Array<GetValidataQuery_validata_getConfidenceSignalsByFields_confidenceSignals>,
      string
    >({
      query: (addressToken) => ({
        body: getValidataQuery,
        variables: {
          addressToken: addressToken,
        },
      }),
      transformResponse: (
        response: GetValidataQuery,
      ): Array<GetValidataQuery_validata_getConfidenceSignalsByFields_confidenceSignals> => {
        return response.validata?.getConfidenceSignalsByFields?.confidenceSignals ?? [];
      },
    }),
    getZones: builder.query<Array<Zones_buyer_getZonesByTextQuery_zones>, string>({
      query: (search) => ({
        body: getZones,
        variables: {
          query: search,
        },
      }),
      transformResponse: (response: Zones) => {
        return response.buyer?.getZonesByTextQuery?.zones ?? [];
      },
    }),
    verifyActiveOffer: builder.query<
      VerifyActiveOffer_sellDirect_verifyActiveOffer_offer | null,
      {
        email: string;
        sellerInputUUID?: string;
        addressUUID?: string;
      }
    >({
      query: ({ email, sellerInputUUID, addressUUID }) => ({
        body: getActiveOffer,
        variables: {
          email,
          sellerInputUUID,
          addressUUID,
        },
      }),
      transformResponse: (response: VerifyActiveOffer) => {
        return response?.sellDirect?.verifyActiveOffer?.offer || null;
      },
    }),
    resendActiveOffer: builder.mutation<
      boolean | null,
      {
        offerId: string;
      }
    >({
      query: ({ offerId }) => ({
        body: resendListedOfferEmail,
        variables: {
          offerId,
        },
      }),
      transformResponse: (response: ResendListedOfferEmail) => {
        return response?.sellDirect?.resendListedOfferEmail?.success || null;
      },
    }),
    saveProgress: builder.mutation<
      SaveSellerInputProgress,
      { email: string; sellerInputUuid: string }
    >({
      query: ({ email, sellerInputUuid }) => ({
        body: saveSellerInputProgress,
        variables: {
          email,
          sellerInputUUID: sellerInputUuid,
        },
      }),
    }),
    requestListedOffer: builder.query<
      RequestListedOffer_reception_requestListedOffer_sellerInput | null,
      {
        realtorName: string;
        realtorEmail: string;
        sellerInputUUID: string;
      }
    >({
      query: ({
        realtorName,
        realtorEmail,
        sellerInputUUID,
      }): GqlQueryCall<RequestListedOfferVariables> => ({
        body: requestListedOfferQuery,
        variables: {
          realtorName,
          realtorEmail,
          sellerInputUUID,
        },
      }),
      transformResponse: (response: RequestListedOffer) => {
        return response?.reception?.requestListedOffer?.sellerInput || null;
      },
    }),
    triggerExperimentImpression: builder.query<
      TriggerExperimentImpressionQuery,
      { sellerInputUUID: string; experimentFlag: ExperimentFlag; attributes?: Record<string, any> }
    >({
      query: ({ sellerInputUUID, experimentFlag, attributes }) => ({
        body: triggerExperimentImpressionQueryBody,
        variables: {
          sellerInputUUID,
          experimentInputs: [
            { name: getExperimentKey(experimentFlag), shouldTriggerImpression: true },
          ],
          attributes,
        },
      }),
    }),
    getPropertyFeatureFlags: builder.query<
      GetAmplitudeFeatureFlagsRequest,
      {
        addressToken: string | null;
        keys: string[];
        props?: GoogleProtobuf_StructInput;
      }
    >({
      query: ({
        addressToken,
        keys,
        props,
      }): GqlQueryCall<GetAmplitudeFeatureFlagsRequestVariables> => {
        const primaryId = addressToken ? { addressToken: addressToken } : undefined;

        return {
          body: getAmplitudeFeatureFlagsQuery,
          variables: {
            grain: {
              grain: {
                property: {
                  primaryId,
                },
              },
            },
            keys,
            props,
          },
        };
      },
    }),
    getVariantQuery: builder.query<
      GetVariantRequest,
      {
        experimentEntityId: string | null;
        customerUUID: string | null;
        experimentFlag: ExperimentFlag;
        triggerImpression?: boolean;
        props?: GoogleProtobuf_StructInput;
      }
    >({
      query: ({
        experimentEntityId,
        experimentFlag,
        customerUUID,
        triggerImpression,
        props,
      }): GqlQueryCall<GetVariantRequestVariables> => {
        const primaryId = customerUUID ? { customerUuid: customerUUID } : undefined;
        const temporaryId = experimentEntityId ? { anonymousId: experimentEntityId } : undefined;

        return {
          body: getVariantQuery,
          variables: {
            grain: {
              grain: {
                customer: {
                  primaryId,
                  temporaryId,
                },
              },
            },
            key: getExperimentKey(experimentFlag),
            autoImpression: triggerImpression,
            props,
          },
        };
      },
    }),
    setImpression: builder.query<
      SetImpressionRequest,
      {
        experimentEntityId: string | null;
        customerUUID: string | null;
        experimentFlag: ExperimentFlag;
        variantValue: Exclude<ExperimentVariant, null>;
      }
    >({
      query: ({
        experimentEntityId,
        customerUUID,
        experimentFlag,
        variantValue,
      }): GqlQueryCall<SetImpressionRequestVariables> => {
        const primaryId = customerUUID ? { customerUuid: customerUUID } : undefined;
        const temporaryId = experimentEntityId ? { anonymousId: experimentEntityId } : undefined;

        return {
          body: setImpression,
          variables: {
            grain: {
              grain: {
                customer: {
                  primaryId,
                  temporaryId,
                },
              },
            },
            key: getExperimentKey(experimentFlag),
            variant_value: variantValue,
          },
        };
      },
    }),
    validateEmail: builder.query<{ suggestion: string; valid: boolean }, string>({
      query: (email) => ({
        body: getEmailValidationQuery,
        variables: { email },
      }),
      transformResponse: (response: GetEmailValidationQuery) => {
        const validation = response.external?.mailgun?.validateEmail;
        const suggestion = validation?.didYouMean ?? '';
        const valid = validation?.valid ?? true;
        return { suggestion, valid };
      },
    }),
    updateAnswers: builder.mutation<
      UpdateAnswers,
      {
        uuid: string;
        completedSlug: string;
        submittedAnswers: Record<string, unknown>;
      }
    >({
      query: ({ uuid, completedSlug, submittedAnswers }) => ({
        body: updateAnswersMutation,
        variables: {
          sellerInputUUID: uuid,
          sellerFlowLastCompletedSlug: completedSlug,
          answers: JSON.stringify(submittedAnswers),
        },
      }),
    }),
    modifyAddress: builder.mutation<
      ModifySellerAddress,
      {
        sellerInputUUID: string;
        address: OdProtosReceptionServices_SellerAddressInput;
      }
    >({
      query: ({ sellerInputUUID, address: { city, postalCode, state, street, unit } }) => ({
        body: modifySellerAddress,
        variables: {
          sellerInputUUID,
          address: {
            city,
            postalCode,
            state,
            street,
            unit,
          },
        },
      }),
    }),
    verifyAddress: builder.mutation<
      VerifyAddress,
      {
        sellerInputUUID: string;
        address: SellerInputAddress;
      }
    >({
      query: ({ sellerInputUUID }): GqlQueryCall<VerifyAddressVariables> => ({
        body: verifyAddressMutation,
        variables: {
          sellerInputUUID,
        },
      }),
    }),
    createSellerInput: builder.mutation<
      CreateSellerInputWithTrackingDataFromCookies,
      {
        address: UpdatedAddress;
        channel?: string;
      }
    >({
      query: ({
        channel,
        address: { city, postalCode, state, street, unit },
      }): GqlQueryCall<CreateSellerInputWithTrackingDataFromCookiesVariables> => ({
        body: createSellerInputWithTrackingDataFromCookies,
        variables: {
          input: {
            address: {
              city,
              postalCode,
              state,
              street,
              unit,
            },
            channel: channelStringToEnum(channel),
            analyticsMetadata: {
              addressEntry: {
                product: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product.UNKNOWN,
                url: window.location.href,
              },
            },
          },
        },
      }),
    }),
  }),
});

/**
 * Wrap variant query with some code that checks that we have at least one of
 * experimentEntityId or customerUUID before we query the variant service.
 */
export const useGetVariantQuery: typeof api.useGetVariantQueryQuery = (args, opts) => {
  if (args !== skipToken) {
    const { experimentEntityId, customerUUID } = args;
    if (!experimentEntityId && !customerUUID) {
      if (process.env.NODE_ENV == 'development' && !opts?.skip) {
        // eslint-disable-next-line no-console
        console.info(
          `Skipping request to GetVariantQuery for ${args.experimentFlag} because both customer uuid and experiment entity id are unset`,
        );
      }
      args = skipToken;
    }
  }
  return api.useGetVariantQueryQuery(args, opts);
};

export const useSetImpressionQuery: typeof api.useSetImpressionQuery = (args, opts) => {
  if (args !== skipToken) {
    const { experimentEntityId, customerUUID, variantValue, experimentFlag } = args;
    if (!experimentEntityId && !customerUUID) {
      if (process.env.NODE_ENV == 'development' && !opts?.skip) {
        // eslint-disable-next-line no-console
        console.info(
          `Skipping request to SetImpressionQuery for ${experimentFlag} because both customer uuid and experiment entity id are unset`,
        );
      }
      args = skipToken;
    } else if (!variantValue) {
      if (process.env.NODE_ENV == 'development' && !opts?.skip) {
        // eslint-disable-next-line no-console
        console.warn(
          `Skipping request to SetImpressionQuery for ${experimentFlag} because variantValue is null or not set`,
        );
      }
      args = skipToken;
    }
  }
  return api.useSetImpressionQuery(args, opts);
};

export const {
  // export useGetVariantQuery up above instead
  // useGetVariantQueryQuery,
  useCreateSellerInputMutation,
  useGetBuilderPartnersQuery,
  useGetCustomerUuidQuery,
  useGetHumanQuery,
  useGetMlsesQuery,
  useGetPropertyFeatureFlagsQuery,
  useGetSellerInputUuidQuery,
  useGetSellerInputsQuery,
  useGetSellerLeadQuery,
  useGetZonesQuery,
  useLazyGetSellerInputsQuery,
  useLazyRequestListedOfferQuery,
  useLazySetImpressionQuery,
  useLazyValidateEmailQuery,
  useLazyVerifyActiveOfferQuery,
  useModifyAddressMutation,
  useResendActiveOfferMutation,
  useSaveProgressMutation,
  useTriggerExperimentImpressionQuery,
  useUpdateAnswersMutation,
  useVerifyActiveOfferQuery,
  useVerifyAddressMutation,
} = api;
